import * as analytics from "@/utils/analytics";
import { Integration } from "@cohesive/api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IntegrationIcon,
} from "@cohesive/ui-components";
import { CheckCircle } from "lucide-react";
import { useState } from "react";

interface RequestAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: Integration;
}

enum DialogState {
  EXPLANATION = "explanation",
  COMPLETION = "completion",
}

export const RequestAccessDialog = ({
  open,
  onOpenChange,
  integration,
}: RequestAccessDialogProps) => {
  const [dialogState, setDialogState] = useState<DialogState>(
    DialogState.EXPLANATION,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useCase, setUseCase] = useState("");

  const handleRequestAccess = async () => {
    setIsSubmitting(true);

    try {
      // Track analytics event
      analytics.trackDiscoverableConnectionAccessRequested({
        integration: {
          id: integration.id,
          name: integration.name,
          platform: integration.platform,
        },
        useCase: useCase.trim() || undefined,
      });

      // Simulate API call - replace with actual implementation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Move to completion state
      setDialogState(DialogState.COMPLETION);
    } catch (error) {
      console.error("Failed to request access:", error);
      // Handle error appropriately
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset dialog state and form when closed
    setTimeout(() => {
      setDialogState(DialogState.EXPLANATION);
      setUseCase("");
    }, 200); // Wait for dialog close animation
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      handleClose();
    } else {
      onOpenChange(newOpen);
    }
  };

  if (dialogState === DialogState.COMPLETION) {
    return (
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-md data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0">
          <DialogHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-spectrum-green-light/30 dark:bg-spectrum-green-dark/30/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-lc-active dark:text-lc-active" />
            </div>
            <DialogTitle className="text-center mb-4">
              Integration Request Submitted!
            </DialogTitle>
            <DialogDescription className="text-center mb-2">
              Thanks for your interest in <strong>{integration.name}</strong>.
              We're rolling out new integrations every week, so keep an eye out
              for an email from us when it's ready to connect.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="sm:justify-center">
            <Button onClick={handleClose} className="w-full sm:w-auto">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-lg data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0">
        <DialogHeader className="mb-0">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-spectrum-blue-light/15 dark:bg-spectrum-blue-dark/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <IntegrationIcon
                platform={integration.platform}
                name={integration.name}
                imageURL={integration.metadata?.image_url}
                className="w-8 h-8"
              />
            </div>
            <div className="flex-1">
              <DialogTitle>{integration.name}</DialogTitle>
              <DialogDescription>Available to request</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm text-center bg-muted/50 rounded-lg p-4">
              <strong>{integration.name}</strong> isn't yet available to
              connect, but we add new connectors every week. Let us know what
              you'd like to do with this connector and we'll add it to our list!
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="use-case"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              What would you like to do with {integration.name}?
            </label>
            <textarea
              id="use-case"
              placeholder="e.g., sync customer data, automate workflows, generate reports..."
              value={useCase}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setUseCase(e.target.value)
              }
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none mt-2"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              This helps us prioritize and configure the integration for your
              needs.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestAccess}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? "Submitting Request..." : "Request Integration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
